/**
 * History tab (FR-4.5, DC-1794 demo D4) — the run series.
 *
 * Runs newest-first, each pinned to the catalog version and content digest it
 * judged. A run made against a catalog the organization no longer holds is
 * marked STALE and still listed: hiding it would erase the drift the tab
 * exists to show (FR-4.5 AC fail). The digest is on the page because
 * "assessed against v6" is not a reproducible claim and "assessed against
 * e5d6e557c19c…" is.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import type { RunHistory } from './assessmentTypes';

interface HistoryEnvelope {
  data?: { history?: RunHistory };
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ready'; history: RunHistory };

export const UseCaseHistory: React.FC<{ useCaseId: string }> = ({ useCaseId }) => {
  const { get } = useApiClient();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const res = await get<HistoryEnvelope>(`/api/use-cases/${useCaseId}/assessment-runs`);
      const history = res.data?.history;
      setState(history ? { phase: 'ready', history } : { phase: 'error' });
    } catch {
      setState({ phase: 'error' });
    }
  }, [get, useCaseId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.phase === 'loading') return <div className="uc-history" aria-busy="true" />;

  if (state.phase === 'error') {
    return (
      <div className="uc-history">
        <div className="banking-grid-error" role="alert">
          <p>The run history could not be read.</p>
          <button type="button" className="banking-btn banking-btn-secondary" onClick={() => void load()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { runs, currentCatalog } = state.history;

  if (runs.length === 0) {
    return (
      <section className="uc-history" aria-label="History">
        <p className="uc-history-lede" data-testid="history-empty">
          No assessment has run for this use case. This is an empty history, not a history of empty
          results.
        </p>
      </section>
    );
  }

  return (
    <section className="uc-history" aria-label="History">
      <p className="uc-history-lede">
        Every run is immutable and pinned to what it judged. Re-assessing creates a new run; nothing
        rewrites an old one.
        {currentCatalog && (
          <>
            {' '}
            Your current catalog is {currentCatalog.versionLabel} (
            <code>{currentCatalog.contentDigest.slice(0, 12)}…</code>).
          </>
        )}
      </p>
      <ol className="uc-history-list" data-testid="history-list">
        {runs.map((run) => (
          <li
            key={run.id}
            className={run.stale ? 'uc-history-run is-stale' : 'uc-history-run'}
            data-testid={`history-run-${run.id}`}
          >
            <div className="uc-history-run-head">
              <span className="uc-history-when">{new Date(run.createdAt).toLocaleString()}</span>
              <span className="uc-history-status">{run.status}</span>
              {run.stale && (
                <span className="uc-history-stale-chip" data-testid={`stale-${run.id}`}>
                  stale — earlier catalog
                </span>
              )}
            </div>
            <p className="uc-history-catalog">
              {run.catalogVersionLabel ?? 'catalog version unavailable'} · digest{' '}
              <code>{run.catalogContentDigest.slice(0, 12)}…</code> · engine {run.engineVersion} ·
              run by {run.createdByName}
            </p>
            <p className="uc-history-counts">
              {run.counts.total} findings — {run.counts.satisfied} satisfied, {run.counts.marginal}{' '}
              marginal, {run.counts.notSatisfied} not satisfied,{' '}
              {run.counts.attestationRequired + run.counts.notAssessable} unanswered
              {/* DC-1806: a run's disputes travel with its record forever —
                  a decision listed beside them visibly judged contested
                  evidence. Zero renders nothing. */}
              {run.disputedCount > 0 && (
                <span className="uc-history-disputed" data-testid={`history-disputed-${run.id}`}>
                  {' '}
                  · {run.disputedCount} disputed
                </span>
              )}
            </p>
            {run.decision ? (
              <p className="uc-history-decision">
                Decision: {run.decision.judgement} · {run.decision.owner} ·{' '}
                {new Date(run.decision.decidedAt).toLocaleString()}
              </p>
            ) : (
              <p className="uc-history-decision uc-history-decision-none">No decision recorded.</p>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
};

export default UseCaseHistory;
